import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProvisionJobs, type ProvisionJob } from '@/services'
import { AppButton, EmptyState, FilterChip, StatusBadge, Drawer, Skeleton, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { relTime, formatDateTime } from '@/lib/utils'

const FILTERS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'running', label: 'Corriendo' },
  { id: 'success', label: 'OK' },
  { id: 'error', label: 'Error' },
]

export function ProvisionsPage() {
  const [filter, setFilter] = useState('all')
  const [selected, setSelected] = useState<ProvisionJob | null>(null)

  const { data: result, isLoading } = useQuery({
    queryKey: ['provision-jobs', filter],
    queryFn: () => getProvisionJobs({ status: filter === 'all' ? undefined : (filter as ProvisionJob['status']), pageSize: 100 }),
    refetchInterval: 15_000,
  })

  const jobs = result?.data || []
  const total = result?.count || jobs.length
  const counts = jobs.reduce<Record<string, number>>((acc, job) => {
    acc[job.status] = (acc[job.status] || 0) + 1
    return acc
  }, {})

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Plataforma</div>
          <h1>Provision jobs</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={240} height={14} /> : `${total} jobs · ${counts.error || 0} fallaron · ${counts.running || 0} corriendo`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="refresh">Recargar</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="table-search">
            <Icon name="search" size={14} />
            <input placeholder="Buscar por tenant o job ID…" />
          </div>
          {FILTERS.map((f) => (
            <FilterChip
              key={f.id}
              active={filter === f.id}
              onClick={() => setFilter(f.id)}
              label={f.label}
              count={isLoading ? undefined : f.id === 'all' ? total : counts[f.id] || 0}
            />
          ))}
        </div>

        {isLoading ? (
          <TableSkeleton columns={7} rows={8} />
        ) : jobs.length === 0 ? (
          <EmptyState icon="cpu" title="Sin jobs" desc="No hay jobs de provisión con ese filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Tenant</th>
                <th>Acción</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Duración</th>
                <th>Cuándo</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} onClick={() => setSelected(job)} {...rowActivation(() => setSelected(job))}>
                  <td><span className="code-chip">{job.id.slice(0, 8)}</span></td>
                  <td><span className="cell-primary">{job.tenant_slug || '—'}</span></td>
                  <td><span className="badge badge-outline badge-sm">{job.action}</span></td>
                  <td>
                    {job.status === 'running' ? (
                      <span className="badge badge-warning badge-sm pulse"><span className="dot" />Corriendo</span>
                    ) : (
                      <StatusBadge status={job.status} size="sm" />
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="tabular" style={{ fontSize: 12 }}>{job.duration_ms ? (job.duration_ms / 1000).toFixed(1) + 's' : '—'}</span>
                  </td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(job.created_at)}</span></td>
                  <td>
                    {job.error
                      ? <span style={{ fontSize: 11, color: 'var(--danger-fg)', fontFamily: 'var(--font-mono)' }}>{job.error.slice(0, 50)}</span>
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected ? `Job ${selected.id.slice(0, 8)}` : ''}>
        {selected ? (
          <div className="col gap-4">
            <div className="card">
              <div className="card-body">
                <dl className="dl">
                  <dt>Tenant</dt><dd>{selected.tenant_slug || '—'}</dd>
                  <dt>Acción</dt><dd><span className="badge badge-outline badge-sm">{selected.action}</span></dd>
                  <dt>Status</dt><dd><StatusBadge status={selected.status} size="sm" /></dd>
                  <dt>Iniciado</dt><dd>{formatDateTime(selected.created_at)}</dd>
                  <dt>Duración</dt><dd>{selected.duration_ms ? (selected.duration_ms / 1000).toFixed(2) + ' s' : '—'}</dd>
                </dl>
              </div>
            </div>
            {selected.error ? (
              <div className="card">
                <div className="card-header"><h3 style={{ color: 'var(--danger-fg)' }}>Error</h3></div>
                <div className="card-body">
                  <pre style={{ margin: 0, padding: 14, background: 'var(--danger-bg)', color: 'var(--danger-fg)', fontSize: 12, fontFamily: 'var(--font-mono)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>{selected.error}</pre>
                </div>
              </div>
            ) : null}
            {selected.payload ? (
              <div className="card">
                <div className="card-header"><h3>Request payload</h3></div>
                <div className="card-body">
                  <pre style={{ margin: 0, padding: 14, background: 'var(--bisne-dark)', color: 'var(--text-inverse)', fontSize: 11.5, fontFamily: 'var(--font-mono)', borderRadius: 8, lineHeight: 1.55, overflow: 'auto' }}>{JSON.stringify(selected.payload, null, 2)}</pre>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </>
  )
}
